import {
  buildWallSegments,
  corners2D,
  projectOntoSegmentLine,
} from '../components/3d/utils/interestingWall';

/**
 * Electrical devices — the ONE catalog of the placeable wiring devices:
 * switches, outlets, and the ceiling devices a switch controls or a circuit
 * feeds. Placement (2D gestures, snap configs, default sizes), the 2D symbols
 * and the 3D renderers all read these entries, so a device's size and mount
 * height cannot disagree between the preview, the saved object and the scene.
 *
 * Lights are NOT here: ceiling, recessed and wall lights keep their own types
 * (`ceiling-light`, `wall-light`) and pipelines. They take part in wiring as
 * fixtures (`isWireableFixture`), not as devices.
 *
 * Pure: no React, no three.js, no Firestore.
 */

/**
 * `mount: 'wall'` — snapped flush to a room wall, centered at `mountHeight`
 * above the floor (the height is to the device's CENTER, matching how
 * `getVerticalPosition` reads wall mounts).
 * `mount: 'ceiling'` — seated against the ceiling at the click point.
 * `mount: 'surface'` — set into the horizontal surface under the click: the
 * countertop if one is there, otherwise the floor. Snapped to no wall. A
 * surface device has no `mountHeight` and gets no entry in `OBJECT_CONFIGS`
 * (`wallDeviceSnapConfig` returns null for it), so it takes the table's
 * floor-mounted `default` — which is what keeps it off the wall-snap path —
 * and `supportSurfaceYAt` then lifts it onto whatever it landed on.
 *
 * Heights are the common US defaults, not code minimums:
 *   - switches 1.22 m (48") to center — the same value `add_switches` uses;
 *   - outlets 0.41 m (16") to center.
 * Sizes are standard single-gang plates (2.75" × 4.5") unless noted.
 */
export const ELECTRICAL_DEVICES = {
  outlet: {
    label: 'Outlet',
    mount: 'wall',
    mountHeight: 0.41,
    dimensions: { x: 0.07, y: 0.115, z: 0.04 },
  },
  'gfci-outlet': {
    label: 'GFCI Outlet',
    mount: 'wall',
    mountHeight: 0.41,
    dimensions: { x: 0.07, y: 0.115, z: 0.04 },
  },
  'outlet-240v': {
    label: '240V Outlet',
    mount: 'wall',
    mountHeight: 0.41,
    dimensions: { x: 0.07, y: 0.115, z: 0.04 },
  },
  'surface-outlet': {
    label: 'Surface Outlet',
    mount: 'surface',
    // ~4" round cover plate set flush INTO whatever horizontal surface it is
    // dropped on — a floor slab or a countertop.
    dimensions: { x: 0.1, y: 0.012, z: 0.1 },
  },
  switch: {
    label: 'Switch',
    mount: 'wall',
    mountHeight: 1.22,
    dimensions: { x: 0.07, y: 0.11, z: 0.04 },
  },
  dimmer: {
    label: 'Dimmer',
    mount: 'wall',
    mountHeight: 1.22,
    dimensions: { x: 0.07, y: 0.11, z: 0.04 },
  },
  '3way': {
    label: '3-Way Switch',
    mount: 'wall',
    mountHeight: 1.22,
    dimensions: { x: 0.07, y: 0.11, z: 0.04 },
  },
  'exhaust-fan': {
    label: 'Exhaust Fan',
    mount: 'ceiling',
    // ~10" square grille, ~1.5" proud of the ceiling.
    dimensions: { x: 0.26, y: 0.04, z: 0.26 },
  },
  'smoke-detector': {
    label: 'Smoke / CO Detector',
    mount: 'ceiling',
    // ~5" disc, ~1.5" deep.
    dimensions: { x: 0.13, y: 0.04, z: 0.13 },
  },
};

export const SWITCH_TYPES = ['switch', 'dimmer', '3way'];

/** Lower-cased `type` of an object, or ''. */
const typeOf = (obj) =>
  typeof obj?.type === 'string' ? obj.type.toLowerCase() : '';

/** @returns {boolean} True for any catalog device type id. */
export const isElectricalDeviceType = (type) =>
  typeof type === 'string' &&
  Object.prototype.hasOwnProperty.call(ELECTRICAL_DEVICES, type.toLowerCase());

/** @returns {boolean} True for a wall-mounted device type (switches, outlets). */
export const isWallElectricalDevice = (type) =>
  isElectricalDeviceType(type) &&
  ELECTRICAL_DEVICES[type.toLowerCase()].mount === 'wall';

/** @returns {boolean} True for a ceiling-mounted device type. */
export const isCeilingElectricalDevice = (type) =>
  isElectricalDeviceType(type) &&
  ELECTRICAL_DEVICES[type.toLowerCase()].mount === 'ceiling';

/**
 * True for a device set into a horizontal SURFACE — a floor slab or a
 * countertop (the surface outlet). Which surface is decided at placement time
 * from what is under the click, not by the type.
 *
 * Kept as its own predicate rather than "not wall and not ceiling", so adding a
 * fourth mount later cannot silently start reading as a surface device.
 *
 * @returns {boolean}
 */
export const isSurfaceElectricalDevice = (type) =>
  isElectricalDeviceType(type) &&
  ELECTRICAL_DEVICES[type.toLowerCase()].mount === 'surface';

/**
 * True for a placed device object. Also matches the legacy switch shape the
 * generator writes (`category.electrical` / `category.switch` …), so generated
 * switches route through the same renderers as placed ones.
 *
 * @param {Object} obj
 * @returns {boolean}
 */
export const isElectricalDeviceObject = (obj) => {
  if (!obj) return false;
  if (isElectricalDeviceType(typeOf(obj))) return true;
  const category =
    typeof obj.category === 'object' && obj.category !== null
      ? obj.category
      : {};
  return (
    category.electrical === true ||
    Object.keys(ELECTRICAL_DEVICES).some((type) => category[type] === true)
  );
};

/** @returns {boolean} True when the object is a switch, dimmer or 3-way. */
export const isSwitchDevice = (obj) => {
  if (!obj) return false;
  if (SWITCH_TYPES.includes(typeOf(obj))) return true;
  const category =
    typeof obj.category === 'object' && obj.category !== null
      ? obj.category
      : {};
  return category.electrical === true || SWITCH_TYPES.some((t) => category[t]);
};

/**
 * True for a 3-way switch specifically.
 *
 * 3-ways are the one device that legitimately wires to ANOTHER DEVICE: a pair
 * of them controls one load from two places, joined by travelers. Plain
 * switches and dimmers never do — a switch wired to a switch is a mistake, so
 * the traveler rule is scoped to this type rather than to `isSwitchDevice`.
 *
 * @param {Object} obj
 * @returns {boolean}
 */
export const isThreeWayDevice = (obj) => {
  if (!obj) return false;
  if (typeOf(obj) === '3way') return true;
  const category =
    typeof obj.category === 'object' && obj.category !== null
      ? obj.category
      : {};
  return category['3way'] === true;
};

/**
 * True for a traveler: the link between the two switches of a 3-way pair,
 * rather than a switch leg out to a load.
 *
 * @param {Object} a
 * @param {Object} b
 * @returns {boolean}
 */
export const isTravelerPair = (a, b) =>
  isThreeWayDevice(a) && isThreeWayDevice(b) && a?.id !== b?.id;

/**
 * True for something a wire can run TO from a switch: any light fixture, or an
 * exhaust fan. Outlets and detectors are not switched loads.
 *
 * @param {Object} obj
 * @returns {boolean}
 */
export const isWireableFixture = (obj) => {
  if (!obj || isElectricalDeviceObject(obj)) {
    return typeOf(obj) === 'exhaust-fan';
  }
  const type = typeOf(obj);
  const category = obj.category || {};
  return (
    category.lighting === true ||
    category['ceiling-light'] === true ||
    category['wall-light'] === true ||
    type.includes('light') ||
    type.includes('lamp') ||
    type.includes('sconce')
  );
};

/**
 * The snap config a wall device needs (`OBJECT_CONFIGS` shape). The center is
 * set ON the wall surface: `alignToNearestWall` places it at
 * `wallOffset + dims.z / 2` from the wall, and the device renderers build the
 * plate outward from local z = 0, so the offset cancels the half depth.
 *
 * @param {string} type
 * @returns {Object|null}
 */
export const wallDeviceSnapConfig = (type) => {
  if (!isWallElectricalDevice(type)) return null;
  const device = ELECTRICAL_DEVICES[type.toLowerCase()];
  return {
    wallOffset: -device.dimensions.z / 2,
    floorMounted: false,
    mountHeight: device.mountHeight,
    requiresWall: true,
    facesWall: false,
    wallAlignment: 'surface',
    verticalAlignment: 'center',
    mountType: 'wall',
  };
};

/**
 * Narrow a set of candidate walls to the ones belonging to the room the click
 * landed in.
 *
 * A wall between two rooms appears once per room, each copy tagged with its own
 * room center, and the snapper only mounts from a wall's room-interior side. So
 * a click near a shared wall can snap to the NEIGHBOUR's copy, which seats the
 * device facing the room next door — in plan its symbol reads into the wall
 * rather than out into the room. Walls are tagged with `_roomId` at collection,
 * so the host room's copies can be preferred.
 *
 * Returns `walls` unchanged when the point is in no room, so placement outside
 * a room behaves as before.
 *
 * @param {Array} walls - Candidate walls, tagged with `_roomId`
 * @param {string|null} hostRoomId - Room containing the click, if any
 * @returns {Array}
 */
export const wallsForHostRoom = (walls, hostRoomId) => {
  if (!hostRoomId) return walls;
  const hostWalls = (walls || []).filter(
    (wall) => wall?._roomId === hostRoomId
  );
  return hostWalls.length ? hostWalls : walls;
};

/**
 * The room a device mounted on `wall` belongs to.
 *
 * This is the room the device faces into, and it must NOT be re-derived from
 * the click: placing a device means clicking ON a wall, and a room's wall IS
 * its floor-polygon boundary, so the click lands inside or outside by a pixel.
 * On the room this bug was reported in, the wall at z = 9.86 is polygon edge
 * 20 — a click at z = 9.85 tests outside the room and z = 9.86 inside. Whenever
 * it fell outside, `findRoomContainingPoint` returned null, the facing
 * correction had no room to work from, and the device kept the snapper's guess:
 * the outlet symbol pointed into the wall, intermittently, on the same wall.
 *
 * The snapped wall has no such ambiguity — it is the surface the device is
 * mounted on — and every candidate is tagged with `_roomId` at collection.
 *
 * @param {Object} wall - The wall the device snapped to
 * @param {Array} rooms - `structureData.rooms`
 * @returns {Object|null} The wall's room, or null if it cannot be resolved
 */
export const roomForSnappedWall = (wall, rooms) => {
  if (!wall?._roomId) return null;
  return (rooms || []).find((room) => room?.id === wall._roomId) || null;
};

/**
 * Turn a wall device so its face points INTO the room.
 *
 * The snapper picks a wall's interior side by comparing against `_roomCenter`,
 * the mean of the room's wall centers — a CENTROID side test, only valid for a
 * convex room. In an L-shaped room that mean can sit on the far side of one of
 * its own walls, and the device is seated facing out: in plan its symbol reads
 * into the wall. First-person navigation hit the same class of bug and fixed it
 * with WINDING-signed segment normals (`interestingWall.js`, and see
 * reference-docs/first-person-navigation: the centroid test was wrong on 339 of
 * 2435 wall segments). This reuses that: find the floor-polygon edge the device
 * sits on and face it along that edge's inward normal.
 *
 * Returns the rotation unchanged when the room has no usable polygon, so a
 * device placed outside any room keeps whatever the snapper decided.
 *
 * @param {number} rotationY - The snapped rotation (radians)
 * @param {{x:number, z:number}} center - Where the device will sit
 * @param {Object} room - The room the device was placed in
 * @returns {number} The rotation to store
 */
export const faceWallDeviceIntoRoom = (rotationY, center, room) => {
  const inward = inwardWallNormalAt(center, room);
  if (!inward) return rotationY;
  const facing = { x: Math.sin(rotationY), z: Math.cos(rotationY) };
  const dot = facing.x * inward.x + facing.z * inward.z;
  return dot >= 0 ? rotationY : rotationY + Math.PI;
};

/**
 * The inward normal (`{x, z}`, unit) of the room-polygon edge a wall-mounted
 * point sits on, or null when the room has no usable polygon or the point lies
 * along none of its edges.
 *
 * The edge is the nearest by perpendicular distance to the edge's own line,
 * among the edges the point actually lies along (5 cm past either end), with
 * WINDING-signed normals — see `faceWallDeviceIntoRoom` for why a centroid
 * side test is wrong in a non-convex room.
 *
 * @param {{x:number, z:number}} point
 * @param {Object} room
 * @returns {{x:number, z:number}|null}
 */
export const inwardWallNormalAt = (point, room) => {
  const segments = buildWallSegments(corners2D(room || {}));
  let hostSegment = null;
  let best = Infinity;
  for (const segment of segments) {
    const projected = projectOntoSegmentLine(segment, point);
    const along =
      (projected.x - segment.p0.x) * segment.tangent.x +
      (projected.z - segment.p0.z) * segment.tangent.z;
    if (along < -0.05 || along > segment.length + 0.05) continue;
    const distance = Math.hypot(point.x - projected.x, point.z - projected.z);
    if (distance < best) {
      best = distance;
      hostSegment = segment;
    }
  }
  if (!hostSegment) return null;
  // Inward is the mirror of the ring's outward normal.
  return { x: -hostSegment.normal.x, z: -hostSegment.normal.z };
};
